import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import './App.css';

// Dashboard import
import { RealtimeDashboard } from './presentation/dashboard/realtime';
import { OrderList } from './presentation/dashboard/orders';
import { PharmacySettings } from './presentation/dashboard/settings';
import {
  CouponManagement,
  CouponDistribute,
  CouponDistributions,
} from './presentation/dashboard/coupon';
import {
  PointManagement,
  PointDistribute,
  PointHistory,
} from './presentation/dashboard/point';
import PointStatus from './presentation/dashboard/point/PointStatus';
import PointEarnRules from './presentation/dashboard/point/PointEarnRules';
import PayPointStatus from './presentation/dashboard/point/PayPointStatus';
import {
  EventDistributions,
  EventHistory,
} from './presentation/dashboard/events';
import { TargetGroupManagement } from './presentation/dashboard/targetGroup';
import {
  SettlementManagement,
} from './presentation/dashboard/settlement';

// 페이지 컴포넌트 (toss/ 폴더 제거 — 신규 화면은 각 도메인 폴더에, v1 아카이브는 v1/ 서브폴더에)
import HomePage from './presentation/dashboard/home/HomePage';
import RxPage from './presentation/dashboard/rx/RxPage';
import PatientPage from './presentation/dashboard/patient/PatientPage';
import TossMarketing from './presentation/dashboard/marketing/TossMarketing';
import TossRanking from './presentation/dashboard/ranking/TossRanking';
import AdherencePage from './presentation/dashboard/adherence/AdherencePage';
import SalesInsight from './presentation/dashboard/insight/SalesInsight';

// Test - 처방전 분석
import PrescriptionAnalysisUpload from './presentation/test/prescriptionAnalysisUpload';
import PrescriptionAnalysisResult from './presentation/test/prescriptionAnalysisResult';

// App - 결제 (Flutter WebView용)
import { PaymentPage, PaymentResultPage } from './presentation/app/payment';

// Auth Components
import { LoginForm } from './core/components/dashboard/Auth/login';
import { RegisterForm } from './core/components/dashboard/Auth/register';
import { ChangePasswordForm } from './core/components/dashboard/Auth/changePassword';
import ProtectedRoute from './core/components/dashboard/Auth/ProtectedRoute';

// Layout
import MainLayout from './core/components/dashboard/MainLayout/MainLayout';

// Store
import useAuthStore from './core/stores/authStore';

/**
 * 루트 경로 리다이렉트 컴포넌트
 * - 로그인됨 → /dashboard/home
 * - 미로그인 → /login
 */
const RootRedirect = () => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  if (isAuthenticated()) {
    return <Navigate to="/dashboard/home" replace />;
  }
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <Router>
      <div className="App-dashboard">
        <Routes>
          {/* 루트 - 로그인 여부에 따라 분기 */}
          <Route path="/" element={<RootRedirect />} />

          {/* 인증 */}
          <Route path="/login" element={<LoginForm />} />
          <Route path="/register" element={<RegisterForm />} />
          <Route path="/change-password" element={<ChangePasswordForm />} />

          {/* 대시보드 - MainLayout을 layout route로 */}
          <Route path="/dashboard" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="home" replace />} />
            <Route path="home" element={<HomePage />} />
            <Route path="rx" element={<RxPage />} />
            <Route path="patient" element={<PatientPage />} />
            <Route path="adherence" element={<AdherencePage />} />
            <Route path="insight" element={<SalesInsight />} />
            <Route path="marketing" element={<TossMarketing />} />
            <Route path="ranking" element={<TossRanking />} />
            <Route path="realtime" element={<RealtimeDashboard />} />
            <Route path="orders" element={<OrderList />} />
            <Route path="settings" element={<PharmacySettings />} />
            {/* 포인트 */}
            <Route path="points" element={<Navigate to="status" replace />} />
            <Route path="points/status" element={<PointStatus />} />
            <Route path="points/rules" element={<PointEarnRules />} />
            <Route path="points/paypoint" element={<PayPointStatus />} />
            <Route path="points/distribute" element={<PointDistribute />} />
            {/* 더미 화면 정리 (10/15) — 실데이터 화면으로 */}
            <Route path="points/distributions" element={<Navigate to="/dashboard/events/distributions" replace />} />
            <Route path="points/history" element={<PointHistory />} />
            {/* 쿠폰 */}
            <Route path="coupons" element={<Navigate to="distribute" replace />} />
            <Route path="coupons/distribute" element={<CouponDistribute />} />
            <Route path="coupons/distributions" element={<CouponDistributions />} />
            <Route path="coupons/history" element={<Navigate to="/dashboard/events/history" replace />} />
            {/* 이벤트 */}
            <Route path="events/distributions" element={<EventDistributions />} />
            <Route path="events/history" element={<EventHistory />} />
            {/* 타겟 그룹 */}
            <Route path="target-groups" element={<TargetGroupManagement />} />
            {/* 정산 */}
            <Route path="settlement" element={<Navigate to="home" replace />} />
            <Route path="settlement/home" element={<SettlementManagement />} />
            {/* 정산 이력: PG 정산 원장 연동 전까지 내림 (백엔드 10/6 권장) */}
            <Route path="settlement/history" element={<Navigate to="/dashboard/settlement/home" replace />} />
          </Route>

          {/* 테스트 - 처방전 분석 */}
          <Route
            path="/test/prescription"
            element={<PrescriptionAnalysisUpload />}
          />
          <Route
            path="/test/prescription/result"
            element={<PrescriptionAnalysisResult />}
          />

          {/* App - 결제 (Flutter WebView용, 인증 불필요) */}
          <Route path="/app/payment" element={<PaymentPage />} />
          <Route path="/app/payment/result" element={<PaymentResultPage />} />

          {/* 404 - 로그인 페이지로 리다이렉트 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
